/**
 * Event creation types — the wizard's config shape + the structural blank
 * default.
 *
 * Docket 2026-07-26 §1.2.3: the vertical event-type entries that lived here
 * (banking's "Quarterly Financial Wellness Series", retail's "Product
 * Education & Tasting / Try-On", "VIP / Loyalty Member Experiences",
 * "Seasonal & Milestone Shopping", the higher-ed set, …) are DELETED from
 * source — they were one vertical's catalog offered to every tenant. Event-
 * type templates are tenant catalog data and arrive through the typed
 * @v0y4ge/api-client (the event-templates op the Type step already renders
 * and seeds from via `seedFromTemplate`), backed by @v0y4ge/api-mocks;
 * REQ-091 asks the served templates to carry the deleted entries' defaults
 * (method / visibility / waitlist / starter tickets). Only the structural
 * `blank` default remains in source; the type picker renders served templates
 * and falls back to Build-from-scratch while they load.
 */

/* Extracted from screens/events/creation/types.ts (2026-09-02). The wizard's
   step screens were deleted on 2026-08-24; these config shapes outlived it —
   REPEAT_LABELS_ALL is read by the Journey Designer, and CREATION_CONFIGS still
   seeds a new event's defaults. Moved out so the retired `creation/` directory
   could go without taking live code with it. */
import { T } from "@v0y4ge/jrni-ui";
import { t as i18nT } from "../../lib/i18n";

export interface TicketSeed {
  id: string; name: string; desc?: string;
  price?: number; capacity?: number | null; sold?: number;
  color?: string; kind?: "free" | "paid" | "code";
  refundable?: boolean; visibility?: "public" | "hidden";
  perOrderLimit?: number; salesStart?: string; salesEnd?: string;
  accessCode?: string;
}

export interface CreationConfig {
  id: string; label: string; labelKey?: string;
  pillText?: string; pillTone?: string;
  heroFrom?: string; heroTo?: string; heroLetters?: string;
  title?: string; description?: string;
  method: { inPerson: boolean; virtual: boolean };
  visibility: string;
  tags?: string[];
  start?: string; end?: string; repeatIndex?: number;
  capacity?: number | null; waitlist?: number | null;
  tickets?: TicketSeed[];
  questions?: unknown[];
  publishRows?: Record<string, [string, string][]>;
  publishWarnings?: string[];
  slug?: string;
}

// ui-config: config data, not copy. `label` is written into the new event as its template name
// (lib/events-adapter), `visibility` is a stored option value compared by isPublicAnyone, and labelKey
// is the display key.
export const CREATION_CONFIGS: Record<string, CreationConfig> = {
  blank: {
    id: "blank", label: "Build from scratch", labelKey: "events.creation.blank.label", // ui-config: written as the event's template name
    pillText: "Blank", pillTone: "neutral", // ui-config: config data, read by nothing
    heroFrom: T.n2, heroTo: T.n1, heroLetters: "EV",
    title: "", description: "",
    method: { inPerson: true, virtual: false },
    visibility: "Public · Anyone with link", // ui-config: stored option value
    tags: [], repeatIndex: 0, capacity: null, waitlist: null, tickets: [], questions: [],
  },
};

// Verbatim from the prototype — the labels that appear in the Schedule step
// repeat picker. Index = `cfg.repeatIndex`.
//
// The INDEX is the identity (stored as repeatIndex); the label is only what shows. Each element is a
// getter, so `[i]`, `.map`, iteration and JSON read the label in the viewer's language at the moment
// they are read, and the array keeps its length and index order.
const REPEAT_LABEL_TEXT: (() => string)[] = [
  () => i18nT("events.eventConfigTypes.repeat.doesntRepeat", "Doesn't repeat"),
  () => i18nT("events.eventConfigTypes.repeat.weeklySameDay", "Weekly on the same day"),
  () => i18nT("events.eventConfigTypes.repeat.monthlyThisDate", "Monthly on this date"),
  () => i18nT("events.eventConfigTypes.repeat.monthlyThisWeekday", "Monthly on this weekday"),
  () => i18nT("events.eventConfigTypes.repeat.quarterlyThisDate", "Quarterly on this date"),
  () => i18nT("events.eventConfigTypes.repeat.quarterlyThisWeekday", "Quarterly on this weekday"),
  () => i18nT("events.eventConfigTypes.repeat.custom", "Custom…"),
];
export const REPEAT_LABELS_ALL: string[] = REPEAT_LABEL_TEXT.map(() => "");
REPEAT_LABEL_TEXT.forEach((label, index) => {
  Object.defineProperty(REPEAT_LABELS_ALL, index, { get: label, enumerable: true });
});
